import { FormikConfig } from 'formik';
import { memo, useCallback, useMemo, useState } from 'react';
import { useOnValueChange } from '../../hooks/useOnValueChange';
import * as Yup from 'yup';
import { CreateModal } from '../../components/Modal/CreateModal';
import { FormikInput } from '../../components/formik/FormInput';
import { FormikSelect } from '../../components/formik/FormSelect';
import { FormikTextArea } from '../../components/formik/FormTextArea';
import { Option } from '../../components/inputs/Select';
import { T, useT } from '../../translation/src';
import { NavVisAuthResult, NavVisDataset, useNavvisListDatasetsLazyQuery } from '../../types/graphqlTypes';

interface NavVisProjectSelectModalProps {
  open: boolean;
  onClose: () => void;
  authResult: NavVisAuthResult | null;
  baseUrl?: string;
  onSelectDataset: (dataset: NavVisDataset, projectName: string, projectDescription: string) => void;
}

interface FormValues {
  siteId: string;
  datasetName: string;
  projectName: string;
  projectDescription: string;
}

const NavVisProjectSelectModal_: React.FC<NavVisProjectSelectModalProps> = ({ open, onClose, authResult, baseUrl, onSelectDataset }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [datasets, setDatasets] = useState<NavVisDataset[]>([]);
  const [fetchDatasets, { loading: loadingDatasets }] = useNavvisListDatasetsLazyQuery();

  const siteLabel = useT('Site');
  const datasetLabel = useT('Dataset');
  const projectNameLabel = useT('Project name');
  const descriptionPlaceholder = useT('enter a project description', { swc: true });
  const siteRequired = useT('Please select a site');
  const datasetRequired = useT('Please select a dataset');
  const projectNameRequired = useT('Project name is required');

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        siteId: Yup.string().required(siteRequired),
        datasetName: Yup.string().required(datasetRequired),
        projectName: Yup.string().required(projectNameRequired),
        projectDescription: Yup.string(),
      }),
    [siteRequired, datasetRequired, projectNameRequired],
  );

  // Read the one field we need up front so the callback closes over a primitive rather than
  // the whole `authResult` object (which the auth hook re-creates on every render).
  const accessToken = authResult?.accessToken;

  const handleSiteChange = useCallback(
    async (siteId: string) => {
      if (!accessToken || !siteId) {
        setDatasets([]);
        return;
      }

      const result = await fetchDatasets({
        variables: {
          accessToken,
          siteId: parseInt(siteId, 10),
          baseUrl: baseUrl || undefined,
        },
      });

      if (result.data?.navvisListDatasets) {
        setDatasets(result.data.navvisListDatasets);
      }
    },
    [accessToken, baseUrl, fetchDatasets],
  );

  const onSubmit: FormikConfig<FormValues>['onSubmit'] = useCallback(
    async (values) => {
      setIsSubmitting(true);
      try {
        const selectedDataset = datasets.find((d) => d.name === values.datasetName);
        if (selectedDataset) {
          onSelectDataset(selectedDataset, values.projectName, values.projectDescription);
          onClose();
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [datasets, onSelectDataset, onClose],
  );

  const formik: FormikConfig<FormValues> = useMemo(
    () => ({
      initialValues: {
        siteId: '',
        datasetName: '',
        projectName: '',
        projectDescription: '',
      },
      validationSchema,
      onSubmit,
    }),
    [validationSchema, onSubmit],
  );

  // Reset datasets when the modal closes. Tracked during render rather than in an effect so a
  // reopened modal never briefly shows the previous site's datasets.
  useOnValueChange(open, (isOpen) => {
    if (!isOpen) setDatasets([]);
  });

  return (
    <CreateModal<FormValues> open={open} onClose={onClose} title={<T _str="Select NavVis Dataset" />} formik={formik} isSubmitting={isSubmitting || loadingDatasets} createButtonTitle={<T _str="Import" />}>
      <div className="space-y-4">
        <FormikSelect name="siteId" label={siteLabel} placeholder={<T _str="Select a site" />} onChange={(e) => handleSiteChange(e.target.value)}>
          {authResult?.sites.map((site) => (
            <Option key={site.id} value={String(site.id)}>
              {site.name}
            </Option>
          ))}
        </FormikSelect>

        {datasets.length > 0 && (
          <FormikSelect name="datasetName" label={datasetLabel} placeholder={<T _str="Select a dataset" />}>
            {datasets.map((dataset) => (
              <Option key={dataset.name} value={dataset.name}>
                {dataset.name}
              </Option>
            ))}
          </FormikSelect>
        )}

        <FormikInput name="projectName" label={projectNameLabel} />

        <FormikTextArea name="projectDescription" label={<T _str="description (optional)" swc />} placeholder={descriptionPlaceholder} />
      </div>
    </CreateModal>
  );
};

export const NavVisProjectSelectModal = memo(NavVisProjectSelectModal_);
